// HLS / LL-HLS player (init-handoff §4.5). Safari uses native HLS; others use hls.js.
import Hls from "hls.js";
import type { PlaybackEndpoint } from "../api/types.js";
import type { PlayerDriver } from "./types.js";

export class HlsPlayer implements PlayerDriver {
  private hls: Hls | null = null;

  async attach(video: HTMLVideoElement, ep: PlaybackEndpoint, opts: { lowLatency: boolean }): Promise<void> {
    const lowLatency = opts.lowLatency && ep.protocol === "llhls";
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari: native HLS.
      video.src = ep.url;
      await playWhenReady(video);
      return;
    }
    if (!Hls.isSupported()) throw new Error("HLS not supported");
    const hls = new Hls({
      lowLatencyMode: lowLatency,
      liveSyncDurationCount: lowLatency ? 2 : 3,
      enableWorker: true,
    });
    this.hls = hls;
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("HLS manifest timeout")), 10000);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        clearTimeout(timer);
        void playWhenReady(video);
        resolve();
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          clearTimeout(timer);
          reject(new Error(`HLS error: ${data.details}`));
        }
      });
      hls.loadSource(ep.url);
      hls.attachMedia(video);
    });
  }

  destroy(): void {
    this.hls?.destroy();
    this.hls = null;
  }
}

export async function playWhenReady(video: HTMLVideoElement): Promise<void> {
  try {
    await video.play();
  } catch {
    // Autoplay blocked -> retry muted; surface unmute affordance via the muted attr.
    video.muted = true;
    try {
      await video.play();
    } catch {
      /* user gesture required */
    }
  }
}
